import React from 'react';
import moment from 'moment';
import { observer } from 'src/utils/mobx-react';
import { useAppStateContext } from 'src/AppState/AppState';
import Loading from 'src/components/Loading';
import MarketsList from 'src/components/MarketsList/MarketsList';
import Docked from 'src/components/Docked/Docked.jsx';
import { EventListGroupEventItemType } from 'src_common/common/websocket2/modelsApi/EventsCollectionQuery';
import { EventsCollectionQueryModel } from 'src_common/common/websocket2/models/EventsCollectionQueryModel';
import { SportCollection, TwoParticipantsSportsTabState } from './TwoParticipantsSportsTabState';
import { RenderEventsRow } from './RenderEventsRow';
import { MenuScrollRegion } from './MenuScrollRegion';
import { MenuScrollCompetition } from './MenuScrollCompetition';
import { MenuScrollFootballMarkets } from './MenuScrollFootballMarkets';
import { Identfier } from 'src/components/CustomersBar/EventsTab/EventsTab.style';
import { MarketTemplatesRecordType, MarketTemplatesType, findMarket, footballMarketsConfig } from './utilsForMarkets';

function getOrCreate<K, V>(map: Map<K, Array<V>>, key: K): Array<V> {
    const list = map.get(key);
    if (list !== undefined) {
        return list;
    }

    const newList: Array<V> = [];
    map.set(key, newList);
    return newList;
}

const groupEvents = (events: Array<EventListGroupEventItemType>): Map<string, Array<EventListGroupEventItemType>> => {
    const out: Map<string, Array<EventListGroupEventItemType>> = new Map();

    for (const event of events) {
        const date = moment(event.startTime).format('YYYY-MM-DD');
        getOrCreate(out, date).push(event);
    }

    return out;
};

const renderSelectedEvent = (
    sportState: TwoParticipantsSportsTabState,
    userId: number,
    selectedEventId: number
): React.ReactElement | null => {
    return (
        <MarketsList
            userId={userId}
            eventId={selectedEventId}
            onClose={(): void => sportState.onEventClick(selectedEventId)}
        />
    );
};

const renderEventsGroup = (
    sportState: TwoParticipantsSportsTabState,
    userId: number,
    groupEvents: Map<string, Array<EventListGroupEventItemType>>,
    marketTemplates: MarketTemplatesRecordType
): Array<React.ReactElement> => {
    const out = [];
    for (const [date, dateEvents] of groupEvents) {
        const firstEvent = dateEvents[0];
        const market = findMarket(firstEvent, sportState, marketTemplates);

        out.push(
            <table key={date}>
                <tbody>
                    <tr>
                        <td colSpan={2}>
                            <strong>
                                {moment(date).calendar(undefined, {
                                    sameDay: '[Today]',
                                    nextDay: '[Tomorrow]',
                                    nextWeek: 'dddd',
                                    sameElse: 'MMMM DD',
                                })}
                            </strong>
                        </td>
                        {market?.id2.getModel()?.selections.map((sel) => (
                            <Identfier key={sel.id2.rawId}>{sel.identifier}</Identfier>
                        ))}
                    </tr>

                    {dateEvents.map((event) => {
                        const market = findMarket(event, sportState, marketTemplates);

                        return (
                            <RenderEventsRow
                                key={event.id}
                                market={market}
                                userId={userId}
                                eventName={event.name}
                                onEventClick={sportState.onEventClick}
                            />
                        );
                    })}
                </tbody>
            </table>
        );
    }

    return out;
};

const renderEvents = (
    sportState: TwoParticipantsSportsTabState,
    userId: number,
    outList: EventsCollectionQueryModel,
    marketTemplates: MarketTemplatesRecordType
): Array<React.ReactElement> => {
    const out = [];

    for (const sport of outList.value.sports) {
        for (const competition of sport.competitions) {
            out.push(
                <div
                    key={competition.id}
                    className='two-participants-events-event'
                >
                    <h3>{competition.name}</h3>
                    {renderEventsGroup(sportState, userId, groupEvents(competition.events), marketTemplates)}
                </div>
            );
        }
    }

    return out;
};

interface LayoutWrapperPropsType {
    collectionState: SportCollection;
    sportState: TwoParticipantsSportsTabState;
    children: React.ReactNode;
    footballMarkets?: Array<MarketTemplatesType>;
}

const LayoutWrapper = observer('LayoutWrapper', (props: LayoutWrapperPropsType) => {
    const { collectionState, sportState, children, footballMarkets } = props;

    return (
        <div className='two-participants-events'>
            <MenuScrollRegion sportState={sportState} />

            <MenuScrollCompetition
                sportState={sportState}
                collectionState={collectionState}
            />

            <MenuScrollFootballMarkets
                sportState={sportState}
                footballMarkets={footballMarkets}
            />

            <div className='two-participants-events-content'>{children}</div>
        </div>
    );
});

interface PropsType {
    sportId: string;
    userId: number;
    marketTemplates: MarketTemplatesRecordType;
    onGetMarketTemplatesList: () => void;
}

export const TwoParticipantsSportsTab = observer('TwoParticipantsSportsTab', (props: PropsType) => {
    React.useEffect(() => {
        props.onGetMarketTemplatesList();
    }, []);

    const appState = useAppStateContext();

    const [sportState] = React.useState(() => new TwoParticipantsSportsTabState(appState, props.marketTemplates));

    const selectedEventId = appState.routerBackoffice.getParametersNumber('customers', 'event');

    const collectionState = sportState.sports.get(props.sportId);

    const listView = collectionState.listView;

    if (listView === null) {
        return (
            <div className='two-participants-events'>
                <Loading
                    message='Loading events...'
                    active={true}
                />
            </div>
        );
    }

    if (listView.ids2.length === 0) {
        return (
            <LayoutWrapper
                sportState={sportState}
                collectionState={collectionState}
            >
                <div className='alert'>There is no matching events</div>
            </LayoutWrapper>
        );
    }

    return (
        <LayoutWrapper
            sportState={sportState}
            collectionState={collectionState}
            footballMarkets={footballMarketsConfig}
        >
            <div className='two-participants-events-events'>
                {renderEvents(sportState, props.userId, listView, props.marketTemplates)}
            </div>

            {selectedEventId === null ? null : (
                <div className='two-participants-events-selected active'>
                    <Docked>{renderSelectedEvent(sportState, props.userId, selectedEventId)}</Docked>
                </div>
            )}
        </LayoutWrapper>
    );
});
